"use client";
import React, { useState, useMemo, useCallback } from "react";
import {
  Search, Plus, Edit3, Trash2, Grid3X3, Layers, Table,
  CheckCircle, XCircle, LayoutList, Package, RefreshCcw, X,
} from "lucide-react";
import CategoryModal from "./CategoryModal";
import Pagination, { usePagination } from './shared/Pagination';
import { useCategories } from "./context/CategoryContext";
import { useProducts }   from "./context/ProductsContext";
import { useLocation } from "react-router-dom";
const CategoryPage = () => {
  const {
    categories = [],
    loading,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleStatus,
  } = useCategories();

  const { products = [] } = useProducts();

  // ── Product count per category ──────────────────────────────────────────────
  const productCountMap = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const id = String(p.categoryId || p.category_id || "");
      if (id) map[id] = (map[id] || 0) + 1;
    });
    return map;
  }, [products]);

  // ── State ───────────────────────────────────────────────────────────────────
 const location = useLocation();
const [searchTerm, setSearchTerm] = useState(location.state?.prefillSearch || "");
  const [statusFilter,    setStatusFilter]     = useState("all");
  const [sortBy,          setSortBy]           = useState("name");
  const [viewMode,        setViewMode]         = useState("grid");
  const [showAddModal,    setShowAddModal]      = useState(false);
  const [editingCategory, setEditingCategory]  = useState(null);
  const [toast,           setToast]            = useState({ visible: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  }, []);

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const totalCount    = categories.length;
  const activeCount   = categories.filter(c => c.status === "Active").length;
  const inactiveCount = totalCount - activeCount;

  // ── Filtered + sorted ───────────────────────────────────────────────────────
  const filteredCategories = useMemo(() => {
    const parseDate = v => { const t = new Date((v || "").replace(" ", "T")).getTime(); return isNaN(t) ? 0 : t; };
    return categories
      .filter(c => {
        const matchSearch = !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === "all" || c.status === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        if (sortBy === "name")      return (a.name || "").localeCompare(b.name || "");
        if (sortBy === "name-desc") return (b.name || "").localeCompare(a.name || "");
        if (sortBy === "newest")    return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
        if (sortBy === "oldest")    return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
        return 0;
      });
  }, [categories, searchTerm, statusFilter, sortBy]);

  const {
    page: catPage, pageSize: catSize, setPage: setCatPage, setPageSize: setCatSize,
    totalPages: catTotalPages, totalItems: catTotal, paginated: paginatedCats,
  } = usePagination(filteredCategories, 10);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleToggleStatus = async (categoryId, currentStatus) => {
    try {
      await toggleStatus(categoryId, currentStatus);
      showToast(`Status updated to ${currentStatus === "Active" ? "Inactive" : "Active"} successfully!`);
    } catch { showToast("Failed to update status.", "error"); }
  };

  const handleDeleteCategory = async id => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try { await deleteCategory(id); } catch (e) { console.error(e); }
  };

  const handleEditCategory = cat => { setEditingCategory(cat); setShowAddModal(true); };
  const handleAddCategory  = ()  => { setEditingCategory(null); setShowAddModal(true); };

  const handleSaveCategory = async data => {
    try {
      editingCategory ? await updateCategory(editingCategory.id, data) : await addCategory(data);
    } catch (e) { console.error(e); }
    finally { setEditingCategory(null); setShowAddModal(false); }
  };

  const clearFilters = () => { setSearchTerm(""); setStatusFilter("all"); setSortBy("name"); };
  const hasFilters = searchTerm || statusFilter !== "all" || sortBy !== "name";

  // ── Toggle switch ────────────────────────────────────────────────────────────
  const ToggleSwitch = ({ categoryId, status }) => (
    <label className="relative inline-flex items-center cursor-pointer"
      title={status === "Active" ? "Set Inactive" : "Set Active"}>
      <input type="checkbox" checked={status === "Active"}
        onChange={() => handleToggleStatus(categoryId, status)} className="sr-only peer" />
      <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm" />
      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
    </label>
  );

  // ── Grid Card ────────────────────────────────────────────────────────────────
  const CategoryCard = ({ category }) => {
    const isActive = category.status === "Active";
    const count    = productCountMap[String(category.id)] || 0;
    const desc     = category.description || "";
    const isLong   = desc.length > 80;
    const [expanded, setExpanded] = useState(false);

    return (
      <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col transition-all duration-300 group
        ${isActive ? "hover:shadow-lg hover:shadow-teal-100/60 hover:-translate-y-0.5" : "opacity-55 grayscale"}`}>
        <div className="relative w-full h-44 overflow-hidden bg-gray-100 flex-shrink-0">
          <img src={category.image} alt={category.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={e => { e.target.src = "https://placehold.co/400x180?text=No+Image"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
         <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
  <h3 className="text-white font-bold text-[15px] leading-snug drop-shadow line-clamp-1 break-all">
    {category.name}
  </h3>
</div>
          <div className="absolute top-3 right-3">
            <ToggleSwitch categoryId={category.id} status={category.status} />
          </div>
          {count > 0 && (
            <div className="absolute top-3 left-3">
              <span className="flex items-center gap-1 px-2 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold rounded-full">
                <Package className="w-2.5 h-2.5" />{count}
              </span>
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col gap-2.5 flex-1">
          {desc ? (
            <div>
             <p className={`text-xs text-gray-500 leading-relaxed break-all ${expanded ? "" : "line-clamp-2"}`}>
  {desc}
</p>
              {isLong && (
                <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                  className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 mt-0.5 transition-colors">
                  {expanded ? "Show less ↑" : "Read more ↓"}
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No description provided.</p>
          )}
          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 mt-auto">
            <span className="text-[11px] text-gray-400 font-medium">
              {count > 0 ? `${count} product${count !== 1 ? "s" : ""}` : "No products yet"}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => handleEditCategory(category)}
                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDeleteCategory(category.id)}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── List Row — responsive card layout on mobile/tablet ────────────────────────
  const ListRow = ({ category, idx }) => {
    const count    = productCountMap[String(category.id)] || 0;
    const isActive = category.status === "Active";
    return (
      <div className={`bg-white border border-gray-200 rounded-xl px-3 py-3 transition-all duration-200
        ${isActive ? "hover:shadow-sm hover:border-teal-200" : "opacity-55 grayscale"}`}>

        {/* ── Desktop row (md+): single horizontal line ── */}
        <div className="hidden md:flex items-center gap-3">
          <span className="text-xs text-gray-400 w-6 text-center flex-shrink-0">{idx + 1}</span>
          <img src={category.image} alt={category.name}
            className="w-10 h-10 rounded-xl object-cover border border-gray-100 flex-shrink-0"
            onError={e => { e.target.src = "https://placehold.co/44x44?text=?"; }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{category.name}</p>
            <p className="text-xs text-gray-400 truncate mt-0.5">
              {category.description
                ? category.description.split(" ").slice(0, 6).join(" ") + (category.description.split(" ").length > 6 ? "…" : "")
                : "No description"}
            </p>
          </div>
          <span className="text-xs text-gray-500 flex-shrink-0 w-20 text-center">
            {count > 0 ? `${count} product${count !== 1 ? "s" : ""}` : "—"}
          </span>
          <ToggleSwitch categoryId={category.id} status={category.status} />
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => handleEditCategory(category)}
              className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDeleteCategory(category.id)}
              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ── Mobile/Tablet row (<md): two-line card layout ── */}
        <div className="flex md:hidden items-center gap-3">
          <img src={category.image} alt={category.name}
            className="w-12 h-12 rounded-xl object-cover border border-gray-100 flex-shrink-0"
            onError={e => { e.target.src = "https://placehold.co/48x48?text=?"; }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{category.name}</p>
            <p className="text-xs text-gray-400 truncate">
              {category.description
                ? category.description.split(" ").slice(0, 6).join(" ") + (category.description.split(" ").length > 6 ? "…" : "")
                : "No description"}
            </p>
            {count > 0 && (
              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                <Package className="w-2.5 h-2.5" />{count} product{count !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <ToggleSwitch categoryId={category.id} status={category.status} />
            <div className="flex items-center gap-1">
              <button onClick={() => handleEditCategory(category)}
                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDeleteCategory(category.id)}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 space-y-4">

        {/* ── KPI Cards — always 3 cols, shrink padding on small screens ── */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {[
            { label: "Total",    value: totalCount,    icon: LayoutList,  accent: "border-l-teal-500",  color: "text-teal-700",  bg: "bg-teal-50"  },
            { label: "Active",   value: activeCount,   icon: CheckCircle, accent: "border-l-green-500", color: "text-green-600", bg: "bg-green-50" },
            { label: "Inactive", value: inactiveCount, icon: XCircle,     accent: "border-l-red-400",   color: "text-red-500",   bg: "bg-red-50"   },
          ].map(({ label, value, icon: Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm min-w-0`}>
              <div className={`w-7 h-7 sm:w-9 sm:h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${color}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-base sm:text-xl font-bold ${color} leading-tight`}>{value}</p>
                <p className="text-[10px] sm:text-xs text-gray-400 leading-tight truncate">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search & Filter bar — wraps naturally on small screens ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 sm:px-4 py-3 flex flex-wrap gap-2 items-center">

          {/* Search — full width on its own row when wrapped */}
          <div className="relative w-full sm:flex-1 sm:min-w-[160px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search categories…"
              className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition" />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status + Sort side by side, then view toggle + add */}
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
            {/* Status filter */}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="flex-1 sm:flex-none px-2.5 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[110px]">
              <option value="all">All ({totalCount})</option>
              <option value="Active">Active ({activeCount})</option>
              <option value="Inactive">Inactive ({inactiveCount})</option>
            </select>

            {/* Sort */}
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="flex-1 sm:flex-none px-2.5 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[110px]">
              <option value="name">A → Z</option>
              <option value="name-desc">Z → A</option>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>

            {/* Reset */}
            {hasFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1 px-2.5 py-2 text-xs sm:text-sm text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg hover:border-red-200 transition-colors flex-shrink-0">
                <RefreshCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}

            {/* Divider */}
            <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />

            {/* View toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
              {[{ mode: "grid", Icon: Grid3X3 }, { mode: "list", Icon: Layers }, { mode: "table", Icon: Table }].map(({ mode, Icon }) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? "bg-white text-teal-600 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                  title={mode.charAt(0).toUpperCase() + mode.slice(1) + " view"}>
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>

            {/* Add button */}
            <button onClick={handleAddCategory}
              className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 sm:px-4 py-2 rounded-xl font-semibold text-xs sm:text-sm transition-colors shadow-sm flex-shrink-0">
              <Plus className="w-4 h-4" />
              <span>Add</span>
              <span className="hidden sm:inline">Category</span>
            </button>
          </div>
        </div>

        {/* ── Results count ── */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing <span className="font-semibold text-gray-700">{filteredCategories.length}</span> of{" "}
            <span className="font-semibold text-gray-700">{totalCount}</span> categories
          </span>
          {loading && <span className="text-teal-600 font-medium animate-pulse">Refreshing…</span>}
        </div>

        {/* ── Content ── */}
        {filteredCategories.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl py-16 sm:py-20 text-center shadow-sm">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Grid3X3 className="w-7 h-7 sm:w-8 sm:h-8 text-gray-300" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-500 mb-1">No categories found</h3>
            <p className="text-sm text-gray-400 mb-5 px-4">
              {hasFilters ? "Try adjusting your search or filters." : "Create your first category to get started."}
            </p>
            {hasFilters ? (
              <button onClick={clearFilters}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                Clear filters
              </button>
            ) : (
              <button onClick={handleAddCategory}
                className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors">
                Add Category
              </button>
            )}
          </div>
        ) : (
          <>
            {/* ── Grid view ── */}
            {viewMode === "grid" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                {paginatedCats.map(cat => <CategoryCard key={cat.id} category={cat} />)}
              </div>
            )}

            {/* ── List view ── */}
            {viewMode === "list" && (
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="p-3 space-y-2">
                  {/* Desktop header — hidden below md */}
                  <div className="hidden md:flex items-center gap-3 px-3 py-3 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
                    <span className="w-6 text-teal-200 text-center">ID</span>
                    <span className="w-10">Image</span>
                    <span className="flex-1">Name / Description</span>
                    <span className="w-20 text-center">Products</span>
                    <span className="w-10 text-center">Status</span>
                    <span className="w-16 text-right">Actions</span>
                  </div>
                  {/* Mobile header — shown below md */}
                  <div className="flex md:hidden items-center px-3 py-2.5 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
                    <span className="flex-1">Categories</span>
                    <span className="mr-14 text-center">Status / Actions</span>
                  </div>
                  {paginatedCats.map((cat, i) => <ListRow key={cat.id} category={cat} idx={i} />)}
                </div>
                <div className="px-3 sm:px-4 border-t border-gray-100">
                  <Pagination currentPage={catPage} totalPages={catTotalPages} totalItems={catTotal}
                    pageSize={catSize} onPageChange={setCatPage} onPageSizeChange={setCatSize} />
                </div>
              </div>
            )}

            {/* ── Table view ── */}
            {viewMode === "table" && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-teal-700 text-white">
                        <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-10">ID</th>
                        <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-14">Image</th>
                        <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                        <th className="px-3 sm:px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Description</th>
                        <th className="px-3 sm:px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Products</th>
                        <th className="px-3 sm:px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider">Status</th>
                        <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedCats.map((cat, i) => {
                        const count    = productCountMap[String(cat.id)] || 0;
                        const isActive = cat.status === "Active";
                        return (
                          <tr key={cat.id}
                            className={`transition-colors hover:bg-teal-50/40 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"} ${!isActive ? "opacity-55 grayscale" : ""}`}>
                            <td className="px-3 sm:px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                            <td className="px-3 sm:px-4 py-3">
                              <img src={cat.image} alt={cat.name}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover border border-gray-100"
                                onError={e => { e.target.src = "https://placehold.co/40x40?text=?"; }} />
                            </td>
                            <td className="px-3 sm:px-4 py-3 font-semibold text-gray-800 text-xs sm:text-sm">{cat.name}</td>
<td className="px-3 sm:px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate break-all hidden lg:table-cell">
  {cat.description || "—"}
</td>                            <td className="px-3 sm:px-4 py-3 text-center hidden sm:table-cell">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                <Package className="w-3 h-3" />{count}
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-center">
                              <ToggleSwitch categoryId={cat.id} status={cat.status} />
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleEditCategory(cat)}
                                  className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors">
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleDeleteCategory(cat.id)}
                                  className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-3 sm:px-4 border-t border-gray-100">
                  <Pagination currentPage={catPage} totalPages={catTotalPages} totalItems={catTotal}
                    pageSize={catSize} onPageChange={setCatPage} onPageSizeChange={setCatSize} />
                </div>
              </div>
            )}

            {/* Pagination for grid only */}
            {viewMode === "grid" && catTotalPages > 1 && (
              <div className="bg-white border border-gray-200 rounded-xl px-3 sm:px-4 shadow-sm">
                <Pagination currentPage={catPage} totalPages={catTotalPages} totalItems={catTotal}
                  pageSize={catSize} onPageChange={setCatPage} onPageSizeChange={setCatSize} />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modal ── */}
      <CategoryModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingCategory(null); }}
        category={editingCategory}
        categories={categories}  
        onSave={handleSaveCategory}
      />

      {/* ── Toast ── */}
      <div style={{
        position: "fixed", bottom: 16, right: 16, zIndex: 9999,
        transition: "all 0.35s ease",
        opacity:   toast.visible ? 1 : 0,
        transform: toast.visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.96)",
        pointerEvents: toast.visible ? "auto" : "none",
      }}>
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold max-w-[calc(100vw-32px)] ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-teal-600 text-white"
        }`}>
          {toast.type === "error"
            ? <XCircle className="w-4 h-4 flex-shrink-0" />
            : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;